import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function QaWorkspace() {
  const navigate = useNavigate();
  const { projectId, subprojectId, sspId } = useParams();
  const companyId = localStorage.getItem('selectedCompanyId');

  const [project, setProject] = useState(null);
  const [subproject, setSubproject] = useState(null);
  const [ssp, setSsp] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  // Create ticket
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium' });
  const [files, setFiles] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const fileRef = useRef();

  // Selection for export
  const [selected, setSelected] = useState(new Set());

  const base = `${API}/companies/${companyId}/qa/projects/${projectId}/subprojects/${subprojectId}/sub-subprojects/${sspId}`;

  useEffect(() => { if (companyId) fetchTickets(); }, [companyId, page, statusFilter, priorityFilter, sort]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchTickets = async () => {
    setLoading(true);
    let url = `${base}/tickets?page=${page}&sort=${sort}`;
    if (statusFilter !== 'all') url += `&status=${statusFilter}`;
    if (priorityFilter) url += `&priority=${priorityFilter}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const res = await authFetch(url);
    if (res && res.ok) {
      const data = await res.json();
      setProject(data.project || null);
      setSubproject(data.subproject || null);
      setSsp(data.sub_subproject || null);
      setTickets(data.tickets?.data || []);
      setPagination({ current_page: data.tickets?.current_page || 1, last_page: data.tickets?.last_page || 1 });
    }
    setLoading(false);
  };

  // Duplicate check
  const checkDuplicates = async (title) => {
    if (title.length < 4) { setDuplicates([]); return; }
    const res = await authFetch(`${base}/tickets/check-duplicates`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title })
    });
    if (res && res.ok) setDuplicates(await res.json());
  };

  const createTicket = async () => {
    if (!form.title.trim() || !form.description.trim()) { window.alert('Title and Description are required.'); return; }

    const fd = new FormData();
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('priority', form.priority);
    files.forEach(f => fd.append('evidence[]', f));

    const token = localStorage.getItem('token');
    const res = await fetch(`${base}/tickets`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: fd
    });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return; }
    if (res.ok) {
      setShowCreate(false);
      setForm({ title: '', description: '', priority: 'medium' });
      setFiles([]);
      setDuplicates([]);
      fetchTickets();
    } else {
      const e = await res.json();
      window.alert(e.message || 'Error creating ticket');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === tickets.length) setSelected(new Set());
    else setSelected(new Set(tickets.map(t => t.id)));
  };

  const exportSelected = async () => {
    if (selected.size === 0) return;
    const res = await authFetch(`${base}/exports`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_ids: [...selected] })
    });
    if (res && res.ok) {
      const data = await res.json();
      window.alert(`Export V${data.export.version} created successfully!`);
      setSelected(new Set());
    } else if (res) {
      const e = await res.json();
      window.alert(e.message || 'Export failed');
    }
  };

  const priorityColor = (p) => ({ critical: '#e74c3c', high: '#e67e22', medium: '#f39c12', low: '#95a5a6' }[p] || '#95a5a6');

  if (!companyId) return <div style={s.page}><p>Please select a company first.</p></div>;
  if (initialLoading && !ssp) return <div style={s.page}><p>Loading workspace...</p></div>;

  return (
    <div style={s.page}>
      {/* Breadcrumb */}
      <div style={s.breadcrumb}>
        <span style={s.breadLink} onClick={() => navigate('/company/qa')}>QA Management</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(`/company/qa/projects/${projectId}`)}>{project?.name}</span>
        <span style={s.breadSep}>/</span>
        <span style={s.breadLink} onClick={() => navigate(`/company/qa/projects/${projectId}/subprojects/${subprojectId}`)}>{subproject?.name}</span>
        <span style={s.breadSep}>/</span>
        <span>{ssp?.name}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={s.title}>{ssp?.name}</h2>
          <span style={{ ...s.badge, backgroundColor: ssp?.status === 'solved' ? '#27ae60' : '#e74c3c' }}>{ssp?.status?.toUpperCase()}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {selected.size > 0 && (
            <button onClick={exportSelected} style={{ ...s.btnPrimary, background: '#8e44ad' }}>
              Export Selected ({selected.size})
            </button>
          )}
          <button onClick={() => navigate(`/company/qa/projects/${projectId}/subprojects/${subprojectId}/workspace/${sspId}/exports`)} style={s.btnSecondary}>
            Export History
          </button>
          <button onClick={() => { setShowCreate(true); setDuplicates([]); }} style={s.btnPrimary}>+ Add Ticket</button>
        </div>
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <input placeholder="Search tickets..." value={search} onChange={e => setSearch(e.target.value)}
               onKeyDown={e => e.key === 'Enter' && fetchTickets()} style={{ ...s.input, flex: 1, minWidth: 180 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={s.select}>
          <option value="all">All Active</option>
          <option value="unsolved">Unsolved</option>
          <option value="solved">Solved</option>
          <option value="archived">Archived</option>
        </select>
        <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }} style={s.select}>
          <option value="">All Priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)} style={s.select}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="most_reported">Most Reported</option>
          <option value="ticket_number">Ticket #</option>
          <option value="priority">Priority</option>
        </select>
        <button onClick={() => { setPage(1); fetchTickets(); }} style={s.btnPrimary}>Search</button>
      </div>

      {/* Ticket List */}
      {initialLoading ? <p>Loading...</p> : tickets.length === 0 ? <p style={{ color: '#999' }}>No tickets found.</p> : (
        <>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={selected.size === tickets.length && tickets.length > 0}
                     onChange={toggleAll} style={{ marginRight: 6 }} />
              Select All
            </label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tickets.map(t => (
              <div key={t.id} style={{ ...s.ticketCard, borderLeft: `4px solid ${priorityColor(t.priority)}`, opacity: t.is_archived ? 0.6 : 1 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                  <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)}
                         style={{ marginTop: 3 }} onClick={e => e.stopPropagation()} />
                  <div style={{ flex: 1, cursor: 'pointer' }}
                       onClick={() => navigate(`/company/qa/projects/${projectId}/subprojects/${subprojectId}/workspace/${sspId}/tickets/${t.id}`)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 700, color: '#2c3e50', fontSize: 14 }}>#{String(t.ticket_number).padStart(3, '0')}</span>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{t.title}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                      <span style={{ ...s.badge, backgroundColor: priorityColor(t.priority) }}>{t.priority.toUpperCase()}</span>
                      <span style={{ ...s.badge, backgroundColor: t.status === 'solved' ? '#27ae60' : '#e74c3c' }}>{t.status.toUpperCase()}</span>
                      {t.report_count > 1 && <span style={{ fontSize: 11, color: '#666' }}>Reported {t.report_count}x</span>}
                      {t.is_archived && <span style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>Archived</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, alignItems: 'center' }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={s.pageBtn}>Prev</button>
          <span>Page {page} of {pagination.last_page}</span>
          <button disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)} style={s.pageBtn}>Next</button>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={{ ...s.modal, maxWidth: 550, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Add Ticket</h3>

            <label style={s.label}>Title *</label>
            <input value={form.title} onChange={e => { setForm({ ...form, title: e.target.value }); checkDuplicates(e.target.value); }}
                   style={s.input} placeholder="Short description of the issue" />

            {/* Duplicate suggestions */}
            {duplicates.length > 0 && (
              <div style={{ background: '#fef9e7', border: '1px solid #f39c12', borderRadius: 6, padding: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#e67e22', marginBottom: 6 }}>Possible Existing Tickets</div>
                {duplicates.map(d => (
                  <div key={d.id} style={{ fontSize: 13, padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                    <span>#{String(d.ticket_number).padStart(3, '0')} — {d.title}</span>
                    <span style={{ ...s.badge, backgroundColor: d.status === 'solved' ? '#27ae60' : '#e74c3c', fontSize: 10 }}>{d.status}</span>
                  </div>
                ))}
              </div>
            )}

            <label style={s.label}>Description *</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      style={{ ...s.input, minHeight: 80 }} placeholder="Detailed explanation of the issue" />

            <label style={s.label}>Priority *</label>
            <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={s.input}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>

            <label style={s.label}>Evidence (optional, max 5MB each)</label>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" multiple ref={fileRef}
                   onChange={e => setFiles([...e.target.files])} style={{ marginBottom: 12 }} />

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={createTicket} style={s.btnPrimary}>Create Ticket</button>
              <button onClick={() => setShowCreate(false)} style={s.btnCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  breadcrumb: { fontSize: 13, color: '#888', marginBottom: 12 },
  breadLink: { color: '#3498db', cursor: 'pointer' },
  breadSep: { margin: '0 6px' },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, color: '#fff', fontSize: 11, fontWeight: 700 },
  filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' },
  input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%', boxSizing: 'border-box', marginBottom: 8 },
  select: { padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 },
  ticketCard: { background: '#fff', borderRadius: 8, padding: '12px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center' },
  btnPrimary: { padding: '8px 18px', background: '#3498db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnSecondary: { padding: '8px 18px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  btnCancel: { padding: '8px 18px', background: '#ecf0f1', color: '#2c3e50', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 },
  pageBtn: { padding: '6px 14px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 500 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, marginTop: 8 },
};